import { useTranslation } from '../../../i18n';
import { KpiTile } from '../KpiTile';
import { StatusDot } from '../StatusDot';
import type { TodayStatus } from '../../../types/dashboard';
import { aggregateAdminKpis, formatHoursDecimal } from '../../../utils/dashboardData';

interface KpiBundle {
  plannedHours: number;
  taskCount: number;
  weekUtilization: number;
}

interface Props {
  rows: TodayStatus[];
  kpis: KpiBundle;
  clockText: string;
}

export function LivePulseHero({ rows, kpis, clockText }: Props) {
  const { t } = useTranslation('dashboard');
  const agg = aggregateAdminKpis(rows);

  return (
    <div className="relative overflow-hidden rounded-2xl bg-ink dark:bg-slate-950 text-white shadow-lg">
      <div className="absolute inset-x-0 top-0 h-1 bg-status-full" aria-hidden />
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr_1fr_1fr] gap-0 lg:gap-px">
        {/* Headline */}
        <div className="p-5 sm:p-6 lg:p-7">
          <div className="flex items-center gap-2 mb-3">
            <StatusDot status="in" pulse size="md" />
            <span
              className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-400"
              role="status"
              aria-live="polite"
            >
              {t('admin.live.kicker', { time: clockText })}
            </span>
          </div>
          {agg.inCount + agg.breakCount === 0 ? (
            <div className="text-2xl sm:text-3xl font-semibold">{t('admin.live.noOne')}</div>
          ) : (
            <>
              <div className="text-2xl sm:text-3xl font-semibold">
                {t('admin.live.title', { count: agg.inCount })}
              </div>
              <div className="mt-1 text-sm text-slate-300">
                {t('admin.live.subtitle', {
                  breakCount: agg.breakCount,
                  absentCount: agg.absentCount,
                  missingCount: agg.missingCount,
                })}
              </div>
            </>
          )}

          <div className="mt-5 flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <StatusDot status="in" />
              <span className="font-mono-num text-slate-300">{agg.inCount}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <StatusDot status="break" />
              <span className="font-mono-num text-slate-300">{agg.breakCount}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <StatusDot status="absent" />
              <span className="font-mono-num text-slate-300">{agg.absentCount}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <StatusDot status="missing" />
              <span className="font-mono-num text-slate-300">{agg.missingCount}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <StatusDot status="out" />
              <span className="font-mono-num text-slate-300">{agg.outCount}</span>
            </div>
          </div>
        </div>

        <HeroStat
          label={t('admin.kpis.plannedToday')}
          value={formatHoursDecimal(kpis.plannedHours)}
          unit="h"
        />
        <HeroStat
          label={t('admin.kpis.tasksToday')}
          value={kpis.taskCount}
        />
        <HeroStat
          label={t('admin.kpis.weekUtilization')}
          value={`${Math.round(kpis.weekUtilization * 100)}`}
          unit="%"
        />
      </div>
    </div>
  );
}

function HeroStat({ label, value, unit }: { label: string; value: string | number; unit?: string }) {
  return (
    <div className="border-t lg:border-t-0 lg:border-l border-white/10 p-5 sm:p-6 bg-white/[0.02]">
      <div className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-2">
        {label}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="font-mono-num text-3xl font-semibold">{value}</span>
        {unit && <span className="font-mono-num text-base text-slate-400">{unit}</span>}
      </div>
    </div>
  );
}

// Re-export KpiTile so the hero file is self-contained if needed elsewhere
export { KpiTile };
